import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SectionDivider,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useApiKey, { useApiKeyForm, useRelations } from '@hooks/useApiKey'
import useCurrentUser from '@hooks/useCurrentUser'

import EntitySelector from '@components/EntitySelector'
import ToggleListItem from '@components/ToggleListItem'

const confirmDelete = (deleteApiKey, deleteFn, closeModal) => {
  const deleteApiKeyPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'API Key Deleted',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, delete it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure? It cannot be recovered.',
      title: 'Delete API Key?',
    })
    .then(({ value }) => {
      if (value){
        deleteApiKey(deleteApiKeyPayload)
      }
    })
}

const createOrUpdateApiKey = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createApiKey, updateApiKey },
  } = props

  const actionFn = entityState.id ? updateFn : createFn
  const toastText = entityState.id ? 'API Key Updated' : 'API Key Created'

  const apiKeyPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.apiKey,
    toastText,
  }

  if (entityState.id) return updateApiKey(apiKeyPayload)

  return createApiKey(apiKeyPayload)
}

const CreateOrEditApiKeyModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditApiKeyModal', showModal } = props
  const { closeModal, deleteApiKey } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { apiKey, userSearchRequestOptions } = modalPayload

  const disableUser = !!apiKey.default_user_id

  const {
    callbacks: {
      createApiKey: createFn,
      deleteApiKey: deleteFn,
      updateApiKey: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useApiKey(apiKey)

  const {
    entityState,
    entityState: {
      default_user_id,
      enabled,
      id,
      owner_id,
      owner_type,
      title,
      token,
    },
    errors,
    handlers,
    saveEnabled: formSaveEnabled,
    setEntityState,
  } = useApiKeyForm(apiKey, { validateOn: 'change' })

  const { user } = useRelations(apiKey)

  // API Key owner set on controller for Admin Users
  const { isAdmin } = useCurrentUser({ id: default_user_id })
  const ownerSaveEnabled = isAdmin ? true : !!owner_id && !!owner_type
  const saveEnabled = formSaveEnabled && ownerSaveEnabled

  const saveOrCreateApiKeyButton = id ? 'Save API Key' : 'Create API Key'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Create'} titleSecondLine="API Key" />

      <SidebarModal.Content>
        <Form data-validate-field-on="change">
          {!disableUser && (
            <EntitySelector
              callbacks={{
                selectItem: apiUser => setEntityState({ default_user_id: apiUser ? apiUser.id : null }),
              }}
              entityKey="users"
              entityTitleKey="full_name"
              errorMessage={errors.default_user_id}
              label="* User"
              searchRequestOptions={userSearchRequestOptions}
              selectedItemId={default_user_id}
            />
          )}

          {disableUser && (
            <Form.Field
              label="User"
            >
              <input disabled name="user" value={user.full_name} />
            </Form.Field>
          )}

          <Form.Field
            direction="column"
            errorMessage={errors.title}
            label="Title"
            marginTop="large"
          >
            <input name="title" value={title} type="text" {...handlers} />
          </Form.Field>

          {id && (
            <>
              <Form.Field
                direction="column"
                label="Token"
                marginTop="large"
              >
                <input disabled name="token" value={token} type="text" {...handlers} />
              </Form.Field>

              <SectionDivider boxProps={{ marginY: 'large' }}>Settings</SectionDivider>

              <ToggleListItem
                onClick={() => setEntityState({ enabled: !enabled })}
                toggleValue={enabled}
              >
                <ToggleListItem.Title>
                  API Access
                </ToggleListItem.Title>
              </ToggleListItem>

              <Form.Field label="More Options">
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  loading={deleting}
                  onClick={() => confirmDelete(deleteApiKey, deleteFn, closeModal)}
                  size="medium"
                >
                  Delete API Key
                </Button>
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateApiKey(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateApiKeyButton : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditApiKeyModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditApiKeyModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
